import { useState } from 'react';
import { resolveApiError, useTranslation } from '@eon/i18n';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  NumberField,
  toast,
} from '@eon/ui';
import { hooks, displayPowerKw, displayEnergyKwh, isActiveSession } from '@eon/api-client';
import type { Device } from '@eon/types';
import { formatKW, formatKWh } from '@eon/utils';
import { IdTagPicker } from '@/components/IdTagPicker';
import { ConnectorStatusBadge } from '@/components/status/StatusBadges';
import { PlugZap, Zap } from 'lucide-react';

const STARTABLE = new Set(['available', 'preparing', 'suspendedev', 'suspendedevse', 'finishing']);

interface DeviceChargingControlsProps {
  device: Device;
}

export function DeviceChargingControls({ device }: DeviceChargingControlsProps) {
  const { t } = useTranslation();
  const ongoing = hooks.useOngoingCharge();
  const start = hooks.useStartCharge();
  const stop = hooks.useStopCharge();
  const [startConnector, setStartConnector] = useState<number | null>(null);
  const [idTag, setIdTag] = useState('');
  const offline = !hooks.isDeviceOnline(device);

  const activeOnDevice =
    ongoing.data?.deviceId === device.id && isActiveSession(ongoing.data.status)
      ? ongoing.data
      : null;

  const onStart = async () => {
    if (startConnector == null || !idTag.trim()) return;
    try {
      await start.mutateAsync({
        deviceId: device.id,
        connectorId: startConnector,
        idTag: idTag.trim(),
      });
      toast.success(t('portal.charging.startSent'));
      setStartConnector(null);
      setIdTag('');
    } catch (err) {
      toast.error(resolveApiError(err, t));
    }
  };

  const onStop = async () => {
    if (!activeOnDevice?.id) return;
    try {
      await stop.mutateAsync(activeOnDevice.id);
      toast.success(t('portal.charging.stopSent'));
    } catch (err) {
      toast.error(resolveApiError(err, t));
    }
  };

  return (
    <>
      <div className="space-y-2">
        {(device.connectors ?? []).map((c) => {
          const status = String(c.status ?? '').toLowerCase();
          const isActiveConnector = activeOnDevice?.connectorId === c.id;
          const canStart = !offline && STARTABLE.has(status) && !activeOnDevice;
          const canStop = !offline && isActiveConnector;

          return (
            <div
              key={c.id}
              className="flex flex-wrap items-center justify-between gap-2 rounded-md border px-2 py-1.5"
            >
              <div className="flex items-center gap-2">
                <ConnectorStatusBadge id={c.id} status={c.status} className="text-[10px]" />
                {c.powerKW != null && c.powerKW > 0 ? (
                  <span className="text-xs font-medium">{formatKW(c.powerKW)}</span>
                ) : isActiveConnector && displayPowerKw(activeOnDevice) != null ? (
                  <span className="text-xs font-medium">
                    {formatKW(displayPowerKw(activeOnDevice))}
                  </span>
                ) : null}
              </div>
              <div className="flex gap-1">
                {canStart ? (
                  <Button size="sm" variant="outline" onClick={() => setStartConnector(c.id)}>
                    <Zap className="mr-1 h-3 w-3" />
                    {t('portal.charging.start')}
                  </Button>
                ) : null}
                {canStop ? (
                  <Button size="sm" variant="outline" onClick={onStop} disabled={stop.isPending}>
                    <PlugZap className="mr-1 h-3 w-3" />
                    {t('portal.charging.stop')}
                  </Button>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={startConnector !== null} onOpenChange={(o) => !o && setStartConnector(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('portal.charging.startTitle', { connector: startConnector ?? 0 })}
            </DialogTitle>
          </DialogHeader>
          <Field label="idTag" required>
            <IdTagPicker value={idTag} onValueChange={setIdTag} />
          </Field>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStartConnector(null)}>
              {t('actions.cancel')}
            </Button>
            <Button onClick={onStart} disabled={start.isPending || !idTag.trim()}>
              {start.isPending ? t('portal.charging.sending') : t('portal.charging.start')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

interface ActiveChargingPanelProps {
  className?: string;
}

export function ActiveChargingPanel({ className }: ActiveChargingPanelProps) {
  const { t } = useTranslation();
  const ongoing = hooks.useOngoingCharge();
  const stop = hooks.useStopCharge();
  const session = ongoing.data;

  if (!session || !isActiveSession(session.status)) {
    return null;
  }

  const power = displayPowerKw(session);
  const energy = displayEnergyKwh(session);

  const onStop = async () => {
    try {
      await stop.mutateAsync(session.id);
      toast.success(t('portal.charging.stopSent'));
    } catch (err) {
      toast.error(resolveApiError(err, t));
    }
  };

  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{t('portal.charging.activeSession')}</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-3 sm:grid-cols-4">
        <div>
          <p className="text-muted-foreground text-xs">{t('session.power')}</p>
          <p className="text-lg font-semibold">{power != null ? formatKW(power) : '—'}</p>
        </div>
        <div>
          <p className="text-muted-foreground text-xs">{t('session.energy')}</p>
          <p className="text-lg font-semibold">{energy != null ? formatKWh(energy) : '—'}</p>
        </div>
        <div>
          <p className="text-muted-foreground text-xs">{t('device.connectors')}</p>
          <p className="text-lg font-semibold">#{session.connectorId}</p>
        </div>
        <div className="flex items-end justify-end">
          <Button variant="outline" size="sm" onClick={onStop} disabled={stop.isPending}>
            <PlugZap className="mr-1.5 h-3.5 w-3.5" />
            {t('portal.charging.stop')}
          </Button>
        </div>
        {session.deviceName || session.stationName ? (
          <p className="text-muted-foreground col-span-full text-xs">
            {[session.stationName, session.deviceName].filter(Boolean).join(' · ')}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
